import { useState, useEffect, useCallback } from 'react';
import {
  IconChevronDownFilled,
  IconCoinFilled,
  IconReceiptFilled,
  IconChartBar,
  IconSearchFilled,
  IconAlertTriangleFilled,
  IconRefresh,
} from '@tabler/icons-react';
import Busqueda from '../components/Busqueda';
import TablaGastos from '../components/TablaGastos';
import DeleteIconCircle from '../components/DeleteIconCircle';
import AgujeroNegro from '../components/AgujeroNegro';
import ModalAgregarGasto from '../components/ModalAgregarGasto';
import ModalEditarGasto from '../components/ModalEditarGasto';
import ModalEliminarGasto from '../components/ModalEliminarGasto';
import EditarAzulButton from '../Utils/EditarAzulButton';
import { ensureMinLoading } from '../Utils/withLoading';
import { apiFetch } from '../api/client';
import TipBlue from '../Utils/TipBlue';

const FONT = '"Public Sans", sans-serif';

function formatMoney(value) {
  return `$${Number(value || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
}

function formatDateOnly(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
  }
  const dt = new Date(value);
  if (isNaN(dt.getTime())) return String(value);
  return dt.toLocaleDateString('es-MX');
}

function tsOf(value) {
  if (!value) return 0;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T12:00:00`).getTime();
  }
  const dt = new Date(value);
  return isNaN(dt.getTime()) ? 0 : dt.getTime();
}

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function hexToRgba(hex, alpha) {
  const h = String(hex || '#8e8e93').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function buildMonthOptions() {
  return Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i, 1).toLocaleDateString('es-MX', { month: 'long' }),
  }));
}

function buildYearOptions() {
  const now = new Date();
  const options = [];
  for (let y = now.getFullYear() - 5; y <= now.getFullYear(); y++) {
    options.push({ value: y, label: String(y) });
  }
  return options.reverse();
}

function MetricCard({ icon: Icon, color, label, value }) {
  return (
    <div
      className="flex flex-col gap-3 rounded-3xl min-w-0"
      style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)', padding: '14px 16px' }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="flex items-center justify-center shrink-0 w-8 h-8 rounded-full"
          style={{ backgroundColor: hexToRgba(color, 0.14) }}
        >
          <Icon size={16} color={color} />
        </span>
        <span
          className="truncate"
          style={{
            fontSize: '11px',
            lineHeight: '14px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.35)',
            fontFamily: FONT,
          }}
        >
          {label}
        </span>
      </div>
      <span
        className="truncate"
        title={String(value)}
        style={{
          fontSize: '20px',
          lineHeight: '24px',
          fontWeight: 600,
          letterSpacing: '-0.02em',
          color: 'rgba(255, 255, 255, 0.95)',
          fontFamily: FONT,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function PeriodoSelect({ label, value, onChange, options, minWidth }) {
  return (
    <div className="relative shrink-0">
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="appearance-none outline-none cursor-pointer touch-manipulation border-none text-white"
        style={{
          height: '48px',
          borderRadius: '9999px',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          padding: '0 44px 0 18px',
          minWidth,
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          fontFamily: FONT,
          color: 'rgba(255, 255, 255, 0.9)',
          WebkitAppearance: 'none',
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>
            {o.label}
          </option>
        ))}
      </select>
      <span
        className="flex items-center justify-center pointer-events-none absolute w-6 h-6 rounded-full"
        style={{
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
        }}
      >
        <IconChevronDownFilled size={12} color="rgba(255, 255, 255, 0.7)" />
      </span>
    </div>
  );
}

function TablaSkeleton() {
  return (
    <div className="flex flex-col gap-1.5" role="status" aria-label="Cargando gastos">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="flex items-center gap-4 rounded-2xl animate-pulse"
          style={{ height: '64px', padding: '0 12px' }}
        >
          <span
            className="shrink-0 rounded-full"
            style={{ width: '22px', height: '22px', backgroundColor: '#141414' }}
          />
          <span
            className="rounded-full"
            style={{ width: '14%', height: '12px', backgroundColor: '#141414' }}
          />
          <span
            className="rounded-full"
            style={{ width: '30%', height: '12px', backgroundColor: '#141414' }}
          />
          <span
            className="ml-auto rounded-full"
            style={{ width: '12%', height: '12px', backgroundColor: '#141414' }}
          />
        </div>
      ))}
    </div>
  );
}

function SinResultados({ query, onClear }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <span
        className="flex items-center justify-center w-11 h-11 rounded-2xl"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)' }}
      >
        <IconSearchFilled size={21} color="rgba(255, 255, 255, 0.3)" />
      </span>
      <div>
        <p
          className="m-0"
          style={{
            fontSize: '15px',
            lineHeight: '20px',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: FONT,
          }}
        >
          Sin resultados
        </p>
        <p
          className="m-0 mt-1"
          style={{
            fontSize: '13px',
            lineHeight: '19px',
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.4)',
            fontFamily: FONT,
          }}
        >
          No encontramos gastos para «{query}»
        </p>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="flex items-center justify-center h-10 px-4 mt-1 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.97] [@media(hover:hover)]:hover:bg-white/[0.1]"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'rgba(255, 255, 255, 0.88)',
          fontFamily: FONT,
        }}
      >
        Limpiar búsqueda
      </button>
    </div>
  );
}

function ErrorCarga({ onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <span
        className="flex items-center justify-center w-11 h-11 rounded-2xl"
        style={{ backgroundColor: 'rgba(255, 69, 58, 0.1)' }}
      >
        <IconAlertTriangleFilled size={20} color="#ff453a" />
      </span>
      <div>
        <p
          className="m-0"
          style={{
            fontSize: '15px',
            lineHeight: '20px',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: FONT,
          }}
        >
          No pudimos cargar los gastos
        </p>
        <p
          className="m-0 mt-1"
          style={{
            fontSize: '13px',
            lineHeight: '19px',
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.4)',
            fontFamily: FONT,
          }}
        >
          Revisa la conexión e inténtalo de nuevo
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="flex items-center justify-center gap-1.5 h-11 px-4 mt-1 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.97] [@media(hover:hover)]:hover:bg-white/[0.1]"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'rgba(255, 255, 255, 0.9)',
          fontFamily: FONT,
        }}
      >
        <IconRefresh size={15} color="rgba(255, 255, 255, 0.6)" />
        Reintentar
      </button>
    </div>
  );
}

export default function Gastos({ notify }) {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return new Date().getMonth() + 1;
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    return new Date().getFullYear();
  });
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowsToDelete, setRowsToDelete] = useState([]);
  const [search, setSearch] = useState('');

  const monthValue = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
  const dateFrom = `${monthValue}-01`;
  const nextMonthDate = new Date(selectedYear, selectedMonth, 1);
  const dateTo = `${nextMonthDate.getFullYear()}-${String(nextMonthDate.getMonth() + 1).padStart(2, '0')}-01`;
  const monthLabel = buildMonthOptions().find((o) => o.value === selectedMonth)?.label || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await apiFetch(`/expenses?limit=500&date_from=${dateFrom}&date_to=${dateTo}`);
      if (!res.ok) throw new Error('fetch');
      const data = await res.json();
      setExpenses(Array.isArray(data) ? data : data.expenses || []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const rows = expenses
    .map((e) => {
      const rawAmount = Number(e.total_amount != null ? e.total_amount : e.amount) || 0;
      return {
        id: e.id,
        expense_id: e.id,
        date: formatDateOnly(e.expense_date),
        sortTs: tsOf(e.expense_date),
        description: e.description,
        category: e.category_name || 'Sin categoría',
        amount: formatMoney(rawAmount),
        amountRaw: String(rawAmount),
      };
    })
    .sort((a, b) => b.sortTs - a.sortTs);

  const totalGastos = expenses.reduce(
    (sum, e) => sum + (Number(e.total_amount != null ? e.total_amount : e.amount) || 0),
    0
  );
  const averageGasto = rows.length > 0 ? totalGastos / rows.length : 0;

  const normalizedQuery = normalizeText(search.trim());
  const filteredRows = normalizedQuery
    ? rows.filter((row) =>
        [row.description, row.category, row.date, row.amountRaw].some((field) =>
          normalizeText(field).includes(normalizedQuery)
        )
      )
    : rows;

  const countLabel = normalizedQuery
    ? `${filteredRows.length} ${filteredRows.length === 1 ? 'resultado' : 'resultados'}`
    : `${rows.length} ${rows.length === 1 ? 'gasto' : 'gastos'}`;

  const openDeleteModal = (selectedRows) => {
    setRowsToDelete(selectedRows || []);
    setDeleteModalOpen(true);
  };

  const handleEdit = (row) => {
    const raw = expenses.find((e) => e.id === row.expense_id);
    if (raw) {
      setExpenseToEdit(raw);
      setEditModalOpen(true);
    }
  };

  const columns = [
    { key: 'date', label: 'Fecha', flex: 0.8, minWidth: '100px' },
    { key: 'description', label: 'Descripción', flex: 2.5, minWidth: '200px' },
    { key: 'category', label: 'Categoría', flex: 1, minWidth: '120px', badge: true },
    { key: 'amount', label: 'Monto', flex: 1, minWidth: '110px' },
    {
      key: 'editar',
      label: '',
      flex: 0,
      minWidth: '90px',
      render: (row) => <EditarAzulButton size="lg" onClick={() => handleEdit(row)} />,
    },
    {
      key: 'eliminar',
      label: '',
      flex: 0,
      minWidth: '50px',
      render: (row) => (
        <DeleteIconCircle solid size={44} onClick={() => openDeleteModal([row])} />
      ),
    },
  ];

  const handleDeleteConfirm = async () => {
    const ids = rowsToDelete.map((r) => r.expense_id);
    setDeleteModalOpen(false);
    setRowsToDelete([]);
    const startedAt = Date.now();
    notify?.('loading', `Eliminando ${ids.length} ${ids.length === 1 ? 'gasto' : 'gastos'}...`);
    let failed = 0;
    for (const id of ids) {
      try {
        const res = await apiFetch(`/expenses/${id}`, { method: 'DELETE' });
        if (!res.ok) failed++;
      } catch {
        failed++;
      }
    }
    await ensureMinLoading(startedAt);
    notify?.('clear');
    setRefreshKey((k) => k + 1);
    if (failed === 0) {
      notify?.('success', `${ids.length} ${ids.length === 1 ? 'gasto eliminado' : 'gastos eliminados'} correctamente`);
    } else if (failed < ids.length) {
      notify?.('error', `${failed} ${failed === 1 ? 'gasto no pudo' : 'gastos no pudieron'} ser eliminados`);
    } else {
      notify?.('error', 'No se pudieron eliminar los gastos. Si el problema persiste, reporta el error.');
    }
  };

  const metricCards = [
    {
      key: 'total',
      label: 'Total del mes',
      value: loading ? '—' : formatMoney(totalGastos),
      icon: IconCoinFilled,
      color: '#f87c2b',
    },
    {
      key: 'registros',
      label: 'Registros',
      value: loading ? '—' : rows.length,
      icon: IconReceiptFilled,
      color: '#0a84ff',
    },
    {
      key: 'promedio',
      label: 'Promedio',
      value: loading ? '—' : formatMoney(averageGasto),
      icon: IconChartBar,
      color: '#bf5af2',
    },
  ];

  return (
    <div className="flex flex-col h-screen bg-black overflow-hidden">
      <header className="shrink-0 px-4 lg:px-6 pt-5 pb-4 border-b border-white/[0.16]">
        <div className="min-w-0">
          <h1
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Gastos
          </h1>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            Compras y gastos registrados
          </p>
        </div>
      </header>

      <div className="flex flex-col flex-1 min-h-0 px-4 lg:px-6 pt-5 pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 shrink-0">
          {metricCards.map((card) => (
            <MetricCard
              key={card.key}
              icon={card.icon}
              color={card.color}
              label={card.label}
              value={card.value}
            />
          ))}
        </div>

        <div className="flex items-center gap-3 shrink-0 pt-4 pb-4">
          <div className="flex-1 max-w-[560px]">
            <Busqueda
              size="lg"
              value={search}
              onChange={setSearch}
              placeholder="Buscar gasto, categoría o fecha"
            />
          </div>
          <span
            className="ml-auto shrink-0 hidden sm:inline"
            style={{
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.35)',
              fontFamily: FONT,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {loading ? 'Cargando…' : countLabel}
          </span>
        </div>

        <div className="flex-1 min-h-0" aria-busy={loading}>
          {loading ? (
            <div className="h-full overflow-y-auto pedidos-scroll">
              <TablaSkeleton />
            </div>
          ) : (
            <TablaGastos
              columns={columns}
              rows={filteredRows}
              selectable
              onDelete={openDeleteModal}
              emptyComponent={
                normalizedQuery ? (
                  <SinResultados query={search.trim()} onClear={() => setSearch('')} />
                ) : loadError ? (
                  <ErrorCarga onRetry={() => setRefreshKey((k) => k + 1)} />
                ) : (
                  <AgujeroNegro
                    message={`No hay gastos registrados en ${monthLabel} ${selectedYear}. Tus compras aparecerán aquí.`}
                  />
                )
              }
            />
          )}
        </div>
      </div>

      <div className="shrink-0 px-4 lg:px-6 py-4 border-t border-white/[0.16] flex items-center justify-between gap-4">
        <TipBlue>Usa Agregar para registrar nuevos gastos y los selectores para filtrar por mes y año.</TipBlue>
        <div className="flex items-center gap-2 shrink-0">
          <PeriodoSelect
            label="Mes"
            value={selectedMonth}
            onChange={setSelectedMonth}
            options={buildMonthOptions()}
            minWidth="150px"
          />
          <PeriodoSelect
            label="Año"
            value={selectedYear}
            onChange={setSelectedYear}
            options={buildYearOptions()}
            minWidth="110px"
          />
          <button
            type="button"
            onClick={() => setAddModalOpen(true)}
            className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none font-semibold transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-[#0077ed]"
            style={{
              backgroundColor: '#0071e3',
              color: '#fff',
              fontSize: '15px',
              letterSpacing: '-0.01em',
              fontFamily: FONT,
            }}
          >
            Agregar
          </button>
        </div>
      </div>

      <ModalAgregarGasto
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onCreated={() => setRefreshKey((k) => k + 1)}
        notify={notify}
      />
      <ModalEditarGasto
        isOpen={editModalOpen}
        onClose={() => { setEditModalOpen(false); setExpenseToEdit(null); }}
        onSaved={() => setRefreshKey((k) => k + 1)}
        notify={notify}
        expense={expenseToEdit}
      />
      <ModalEliminarGasto
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setRowsToDelete([]); }}
        onConfirm={handleDeleteConfirm}
        itemCount={rowsToDelete.length}
        codes={rowsToDelete.map((r) => r.expense_id)}
      />
    </div>
  );
}
